import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Phone, MessageCircle, ShieldCheck, ChevronDown, MapPin, Navigation, Wrench, Sparkles, Heart, Leaf } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeSwitcher from "@/components/ThemeSwitcher";
import BrandMark from "@/components/BrandMark";
import SiteFooter from "@/components/SiteFooter";
import { useClientTheme } from "@/contexts/ThemeContext";
import {
  BRAND_TAGLINE_IT, BRAND_TAGLINE_EN, LOGIN_PHOTOS,
  BRAND_PHONE, BRAND_PHONE_DISPLAY, BRAND_WHATSAPP, BRAND_WHATSAPP_DISPLAY,
  BRAND_NAME, BRAND_ADDRESS_STREET, BRAND_ADDRESS_CITY,
  BRAND_MAPS_URL, BRAND_WAZE_URL, BRAND_APPLE_MAPS_URL, BRAND_MAP_EMBED_URL,
} from "@/lib/brand";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login, verifyOtp } = useAuth();
  const { theme } = useClientTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Iter 49 — public catalog. When a guest clicks "book" on a scooter they are
  // sent here with ?returnTo=/scooter/:id?start_at=…&end_at=… so after login
  // we can drop them back on the exact vehicle + dates they had picked.
  const rawReturnTo = searchParams.get("returnTo") || "";
  const returnTo = rawReturnTo.startsWith("/") ? rawReturnTo : "";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [photoIdx, setPhotoIdx] = useState(0);
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [otpCode, setOtpCode] = useState("");

  // Preload + rotate every 10s
  useEffect(() => {
    LOGIN_PHOTOS.forEach((src) => { const i = new Image(); i.src = src; });
    const id = setInterval(() => setPhotoIdx((i) => (i + 1) % LOGIN_PHOTOS.length), 10000);
    return () => clearInterval(id);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.otp_required) {
      setOtpChallenge(res.challenge_id);
      toast.info(i18n.language === "en" ? "Verification code sent on Telegram" : "Codice di verifica inviato su Telegram");
      return;
    }
    if (res.ok) {
      toast.success(t("auth.welcome_back"));
      if (res.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate(returnTo || "/home");
      }
    } else {
      toast.error(res.error);
    }
  };

  const onSubmitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await verifyOtp(otpChallenge, otpCode);
    setLoading(false);
    if (res.ok) {
      toast.success(t("auth.welcome_back"));
      if (res.user.role === "admin") {
        navigate("/admin");
      } else {
        navigate(returnTo || "/home");
      }
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-theme={theme} data-testid="login-root">
      {/* ───────── HERO + LOGIN ───────── */}
      <section className="min-h-screen flex items-center justify-center px-5 py-10 relative overflow-hidden">
      {/* Marciana Marina hero photo carousel (rotates every 10s, crossfade) */}
      {LOGIN_PHOTOS.map((src, i) => {
        const isActive = i === photoIdx;
        return (
          <div
            key={src}
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `url('${src}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: isActive ? 0.55 : 0,
              // Long crossfade — opacity AND a slow Ken-Burns zoom for cinematic feel.
              transform: isActive ? "scale(1.04)" : "scale(1.0)",
              transition: "opacity 2200ms cubic-bezier(0.4, 0, 0.2, 1), transform 12000ms ease-out",
              willChange: "opacity, transform",
            }}
          />
        );
      })}
      {/* Vignette gradient overlay so the form stays legible.
          Cream/cinematic look adapts to the active theme. */}
      <div className="absolute inset-0 pointer-events-none"
           style={{
             background: theme === "light"
               ? "radial-gradient(ellipse at center, rgba(245,244,239,0.0) 0%, rgba(245,244,239,0.55) 60%, rgba(245,244,239,0.92) 100%)"
               : "radial-gradient(ellipse at center, rgba(9,9,11,0.15) 0%, rgba(9,9,11,0.55) 60%, rgba(9,9,11,0.85) 100%)",
           }} />

      {/* Carousel indicator dots */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
        {LOGIN_PHOTOS.map((src, i) => (
          <button
            key={src}
            type="button"
            onClick={() => setPhotoIdx(i)}
            aria-label={`photo ${i + 1}`}
            className={`h-1.5 rounded-full transition-all ${i === photoIdx ? "w-6 bg-[#FFD700]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
            data-testid={`login-photo-dot-${i}`}
          />
        ))}
      </div>

      <div className="absolute top-5 right-5 z-10 flex items-center gap-2">
        <ThemeSwitcher />
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md relative">
        <div className="mb-8 text-center">
          <div className="inline-flex flex-col items-center gap-2 mb-5">
            <BrandMark size="xl" showWordmark={false} />
            <h2
              className="mt-3 text-5xl sm:text-6xl text-[#FFD700] leading-none drop-shadow-[0_4px_24px_rgba(250,204,21,0.35)]"
              style={{
                fontFamily: "'Caveat', cursive",
                fontWeight: 700,
                transform: "rotate(-3deg)",
                letterSpacing: "0.5px",
              }}
              data-testid="login-brand-script"
            >
              Rent Lupi
            </h2>
            <p
              className="mt-3 max-w-xs text-balance text-zinc-100 leading-snug px-4"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
                fontSize: "1.05rem",
              }}
              data-testid="login-brand-tagline"
            >
              {i18n.language === "en" ? (
                <>The most beautiful way to discover Elba: <span className="text-[#FFD700]">Rent, Explore, Smile!</span></>
              ) : (
                <>Il modo più bello di scoprire l&apos;Elba: <span className="text-[#FFD700]">Noleggia, Esplora, Sorridi!</span></>
              )}
            </p>
            <span className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-2">
              {i18n.language === "en" ? BRAND_TAGLINE_EN : BRAND_TAGLINE_IT}
            </span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-6">{t("auth.login_title")}</h1>
          <p className="text-zinc-400 mt-2 text-sm">{t("auth.login_subtitle")}</p>
        </div>

        <form onSubmit={otpChallenge ? onSubmitOtp : onSubmit} className="bg-zinc-950/80 backdrop-blur-md border border-zinc-800/80 rounded-xl p-6 space-y-4 shadow-2xl shadow-black/50">
          {otpChallenge ? (
            <>
              <div className="text-center pb-2">
                <ShieldCheck className="w-10 h-10 mx-auto text-[#FFD700] mb-2" />
                <h2 className="font-display text-xl font-bold">{i18n.language === "en" ? "Two-factor verification" : "Verifica in due passaggi"}</h2>
                <p className="text-zinc-400 text-xs mt-1">
                  {i18n.language === "en"
                    ? "Insert the 6-digit code sent to the Telegram bot."
                    : "Inserisci il codice a 6 cifre inviato sul bot Telegram."}
                </p>
              </div>
              <div>
                <Label className="text-zinc-300">{i18n.language === "en" ? "Verification code" : "Codice di verifica"}</Label>
                <Input value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                       className="bg-zinc-950 border-zinc-800 mt-1.5 text-center font-mono text-2xl tracking-[0.5em]"
                       placeholder="••••••" autoFocus
                       data-testid="login-otp-input" />
              </div>
              <Button type="submit" disabled={loading || otpCode.length !== 6}
                      className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold disabled:opacity-50"
                      data-testid="login-otp-submit">
                {loading ? (i18n.language === "en" ? "Verifying…" : "Verifica…") : (i18n.language === "en" ? "Verify and login" : "Verifica e accedi")}
              </Button>
              <Button type="button" variant="ghost" onClick={() => { setOtpChallenge(null); setOtpCode(""); }}
                      className="w-full text-zinc-400 hover:text-zinc-100" data-testid="login-otp-cancel">
                {i18n.language === "en" ? "Cancel" : "Annulla"}
              </Button>
            </>
          ) : (
          <>
          <div>
            <Label htmlFor="email" className="text-zinc-300">{t("auth.email")}</Label>
            <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                   className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5"
                   placeholder="tu@email.it" data-testid="login-email-input" />
          </div>
          <div>
            <Label htmlFor="password" className="text-zinc-300">{t("auth.password")}</Label>
            <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                   className="bg-zinc-950 border-zinc-800 focus:ring-[#FFD700]/50 mt-1.5"
                   placeholder="••••••••" data-testid="login-password-input" />
          </div>
          <Button type="submit" disabled={loading}
                  className="w-full bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold"
                  data-testid="login-submit-button">
            {loading ? t("auth.submitting_login") : t("auth.submit_login")}
          </Button>
          </>
          )}

          <div className="text-center text-sm text-zinc-400 space-y-1">
            <div>
              {t("auth.no_account")}{" "}
              <Link to={`/register${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`} className="text-[#FFD700] hover:underline" data-testid="login-go-to-register">
                {t("auth.register_cta")}
              </Link>
            </div>
            <div>
              <Link to="/forgot-password" className="text-zinc-500 hover:text-[#FFD700] text-xs" data-testid="login-forgot-password">
                {t("auth.forgot_password")}
              </Link>
            </div>
            <div>
              <Link to="/admin/recover" className="text-zinc-600 hover:text-[#FFD700] text-[10px] uppercase tracking-widest" data-testid="login-admin-recover">
                {i18n.language === "en" ? "Account recovery (staff)" : "Recupero account (staff)"}
              </Link>
            </div>
          </div>
        </form>

        {/* Direct contact bar */}
        <div className="mt-5 flex items-center gap-2">
          <a href={`tel:${BRAND_PHONE}`}
             className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-zinc-950/80 backdrop-blur-md border border-zinc-800 hover:border-[#FFD700]/50 hover:text-[#FFD700] text-zinc-300 text-sm transition-colors"
             data-testid="contact-phone">
            <Phone className="w-4 h-4" />
            <span className="font-medium">{BRAND_PHONE_DISPLAY}</span>
          </a>
          <a href={`https://wa.me/${BRAND_WHATSAPP.replace(/[^0-9]/g, "")}`}
             target="_blank" rel="noreferrer"
             className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#25D366]/15 backdrop-blur-md border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-sm font-medium transition-colors"
             data-testid="contact-whatsapp">
            <MessageCircle className="w-4 h-4" />
            <span>{BRAND_WHATSAPP_DISPLAY}</span>
          </a>
        </div>

        {/* Scroll cue */}
        <a href="#chi-siamo"
           className="mt-8 flex flex-col items-center text-zinc-500 hover:text-[#FFD700] transition-colors animate-pulse"
           data-testid="login-scroll-cue">
          <span className="text-[10px] uppercase tracking-[0.3em]">{i18n.language === "en" ? "Scroll" : "Scopri di più"}</span>
          <ChevronDown className="w-5 h-5 mt-1" />
        </a>
      </div>
      </section>

      {/* ───────── CHI SIAMO ───────── */}
      <section id="chi-siamo" className="relative px-5 py-20 sm:py-24 border-t border-white/5"
               data-testid="login-about">
        <div className="max-w-3xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFD700] mb-3">
            {i18n.language === "en" ? "About us" : "Chi siamo"}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight">
            {i18n.language === "en" ? (
              <>A small family workshop, <span className="text-[#FFD700]">on Elba since 1982</span>.</>
            ) : (
              <>Una piccola realtà familiare, <span className="text-[#FFD700]">all&apos;Elba dal 1982</span>.</>
            )}
          </h2>
          <p className="mt-5 text-zinc-300 leading-relaxed text-base sm:text-lg">
            {i18n.language === "en"
              ? "Born in Marciana Marina in 1982 as a mechanical workshop, Lupi grew alongside the island and its visitors. Today we run the historic workshop and an authorised inspection centre, and we rent scooters and cars during the summer season — keeping the same care for every single customer we had on day one."
              : "Nati a Marciana Marina nel 1982 come officina meccanica, siamo cresciuti insieme all'isola e ai suoi visitatori. Oggi gestiamo la storica officina e un centro revisioni autorizzato, e noleggiamo scooter e auto nella stagione estiva — con la stessa attenzione per ogni singolo cliente di quando abbiamo aperto."}
          </p>

          {/* Pillars */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5"
                 data-testid="about-pillar-craftsmanship">
              <Wrench className="w-5 h-5 text-[#FFD700] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-1">
                {i18n.language === "en" ? "Real workshop" : "Officina vera"}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {i18n.language === "en"
                  ? "Every vehicle is serviced in-house by our mechanics. No outsourcing, no surprises on the road."
                  : "Ogni veicolo è preparato in casa dai nostri meccanici. Niente terzi, niente sorprese in viaggio."}
              </p>
            </div>
            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5"
                 data-testid="about-pillar-progress">
              <Sparkles className="w-5 h-5 text-[#FFD700] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-1">
                {i18n.language === "en" ? "Attentive to progress" : "Attenti al progresso"}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {i18n.language === "en"
                  ? "Online booking, instant deposit, digital contract with signature on tablet — your rental, with no paperwork."
                  : "Prenotazione online, caparra istantanea, contratto digitale con firma su tablet — il tuo noleggio, senza carte da riempire."}
              </p>
            </div>
            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5"
                 data-testid="about-pillar-care">
              <Heart className="w-5 h-5 text-[#FFD700] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-1">
                {i18n.language === "en" ? "One-to-one care" : "Rapporto umano"}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {i18n.language === "en"
                  ? "You always speak to a person, not a call center. We answer the phone and WhatsApp personally."
                  : "Parli sempre con una persona, non con un call center. Rispondiamo noi al telefono e su WhatsApp."}
              </p>
            </div>
            <div className="rounded-2xl bg-zinc-900/60 border border-white/5 p-5"
                 data-testid="about-pillar-island">
              <Leaf className="w-5 h-5 text-[#FFD700] mb-3" />
              <h3 className="font-display text-lg font-semibold mb-1">
                {i18n.language === "en" ? "We live the island" : "Viviamo l'isola"}
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed">
                {i18n.language === "en"
                  ? "We know the back roads, the panoramic loops and the best beaches. Ask us — we'll tell you where to go."
                  : "Conosciamo le strade panoramiche, le calette nascoste e i posti migliori. Chiedici un consiglio — ti diremo dove andare."}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ───────── DOVE SIAMO ───────── */}
      <section className="relative px-5 py-20 sm:py-24 border-t border-white/5 bg-zinc-900/30"
               data-testid="login-find-us">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
              <MapPin className="w-4 h-4 text-[#FFD700]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                {i18n.language === "en" ? "Find us" : "Vieni a trovarci"}
              </p>
              <h2 className="font-display text-3xl font-bold tracking-tight leading-tight">
                {i18n.language === "en" ? "How to reach us" : "Come raggiungerci"}
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <div className="space-y-5">
              <div className="rounded-2xl bg-zinc-950/80 border border-white/5 p-5" data-testid="login-address-card">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  {i18n.language === "en" ? "Address" : "Indirizzo"}
                </p>
                <p className="text-lg font-semibold text-zinc-100">{BRAND_NAME}</p>
                <p className="text-zinc-300 mt-0.5">{BRAND_ADDRESS_STREET}</p>
                <p className="text-zinc-300">{BRAND_ADDRESS_CITY}</p>
                <div className="mt-4 grid grid-cols-3 gap-2">
                  <a href={BRAND_MAPS_URL} target="_blank" rel="noreferrer"
                     className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-[#FFD700] text-zinc-950 font-semibold text-xs hover:brightness-110 transition"
                     data-testid="login-open-gmaps">
                    <Navigation className="w-4 h-4" /> Google Maps
                  </a>
                  <a href={BRAND_WAZE_URL} target="_blank" rel="noreferrer"
                     className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-[#33CCFF]/15 border border-[#33CCFF]/40 text-[#33CCFF] font-semibold text-xs hover:bg-[#33CCFF]/25 transition"
                     data-testid="login-open-waze">
                    <Navigation className="w-4 h-4" /> Waze
                  </a>
                  <a href={BRAND_APPLE_MAPS_URL} target="_blank" rel="noreferrer"
                     className="flex flex-col items-center justify-center gap-1 py-2.5 rounded-xl bg-zinc-800/80 border border-zinc-700 text-zinc-100 font-semibold text-xs hover:bg-zinc-800 transition"
                     data-testid="login-open-apple-maps">
                    <Navigation className="w-4 h-4" /> Apple Maps
                  </a>
                </div>
              </div>

              <div className="rounded-2xl bg-zinc-950/80 border border-white/5 p-5">
                <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">
                  {i18n.language === "en" ? "Walking from Marciana Marina" : "A piedi da Marciana Marina"}
                </p>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  {i18n.language === "en"
                    ? "From the Conad supermarket: 500 metres on foot towards Poggio Marciana, following the SP25 in the opposite direction."
                    : "Dal supermercato Conad: 500 metri a piedi direzione Poggio Marciana, seguendo in controsenso la SP25."}
                </p>
              </div>

              <div className="rounded-2xl bg-zinc-950/80 border border-white/5 p-5 flex items-center gap-3">
                <a href={`tel:${BRAND_PHONE}`}
                   className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-zinc-900 border border-zinc-800 hover:border-[#FFD700]/50 hover:text-[#FFD700] text-zinc-300 text-sm transition-colors"
                   data-testid="login-about-phone">
                  <Phone className="w-4 h-4" />
                  <span className="font-medium">{BRAND_PHONE_DISPLAY}</span>
                </a>
                <a href={`https://wa.me/${BRAND_WHATSAPP.replace(/[^0-9]/g, "")}`}
                   target="_blank" rel="noreferrer"
                   className="flex-1 inline-flex items-center justify-center gap-2 py-2.5 rounded-lg bg-[#25D366]/15 border border-[#25D366]/40 hover:bg-[#25D366]/25 text-[#25D366] text-sm font-medium transition-colors"
                   data-testid="login-about-whatsapp">
                  <MessageCircle className="w-4 h-4" />
                  <span>WhatsApp</span>
                </a>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-white/5 bg-zinc-950/80 min-h-[420px]" data-testid="login-map">
              <iframe
                title="Rent Lupi · Marciana Marina"
                src={BRAND_MAP_EMBED_URL}
                width="100%"
                height="100%"
                style={{ minHeight: 420, border: 0, filter: "invert(0.92) hue-rotate(180deg) saturate(0.7)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Legal footer — mandatory for unauthenticated landing (D.Lgs. 70/2003 art.7,
          GDPR art.13, EU Reg.524/2013 art.14). Shows P.IVA, REA, ODR link. */}
      <SiteFooter />
    </div>
  );
}
